import { useEffect } from 'react';
import { useCommon } from '@context/common';

import Button from '@hiredigital/ui/Button';
import InputContainer from '@hiredigital/ui/Form/InputContainer';
import TextArea from '@hiredigital/ui/Input/TextArea';
import DualInput from '@hiredigital/ui/Input/DualInput';
import { getDefaultCurrencyByCountry } from '@hiredigital/lib/helpers/rates';
import { Currency, ServiceRateType } from '@hiredigital/lib/helpers/marketingEnum';

import Styles from '../Registration.module.scss';
import Wrapper from './Wrapper';

const RateDualInput = ({ service, handleChange, submitted, errors }) => {
  const rate = service.maxRate;
  const invalidRate = !rate || rate <= 0 || isNaN(rate);

  const handleCurrencyChange = (event) => {
    const { value } = event.target;
    handleChange.onCurrencyChange?.(value?.code);
  };

  const handleRateChange = (event) => {
    const { value } = event.target;
    handleChange.onRateChange?.(parseFloat(value));
  };

  return (
    <DualInput
      input={(Input) => (
        <Input
          type='text'
          name='maxRate'
          label='Rate'
          placeholder='Enter your rate'
          defaultValue={invalidRate ? '' : rate}
          onChange={handleRateChange}
          error={(submitted && invalidRate && 'Enter your rate') || errors.rate}
        />
      )}
      select={(Select) => (
        <Select
          id='currencyCodeID'
          name='currencyCode'
          label='Currency'
          style={{ flexBasis: '150px' }}
          value={Currency.getEnum(service.currencyCode) || service.currencyCode || ''}
          // getOptionLabel={({ code, label }) => `${code} - ${label}`}
          formatOptionLabel={({ code, label }, { context }) => (context === 'menu' ? label : code)}
          getOptionValue={({ id }) => id}
          options={Currency.values}
          onChange={handleCurrencyChange}
          error={errors.currency || ''}
          // isClearable
        />
      )}
      secondSelect={(Select) => (
        <Select
          id='rateTypeID'
          name='rateType'
          label='Period'
          style={{ flexBasis: '200px' }}
          value={ServiceRateType.getEnum(service.rateType) || service.rateType || ''}
          getOptionLabel={({ label }) => label}
          getOptionValue={({ id }) => id}
          options={ServiceRateType.values}
          onChange={handleChange.onRateTypeChange}
          error={errors.rateType || ''}
          // isClearable
        />
      )}
    />
  );
};

const RateForm = ({
  embedInDialog,
  onSubmit,
  onBack,
  onCurrencyChange,
  onRateChange,
  onRateTypeChange,
  onRateDescriptionChange,
  currency,
  rate,
  submitted,
  errors,
  rateType,
  name,
  loading,
}) => {
  const common = useCommon();

  useEffect(() => {
    if (currency || !common || !onCurrencyChange) return;
    getDefaultCurrencyByCountry(common, onCurrencyChange);
  }, [currency, common, onCurrencyChange]);

  useEffect(() => {
    if (rateType || !onRateTypeChange) return;
    onRateTypeChange(ServiceRateType.HOUR);
  }, [rateType, onRateTypeChange]);

  const handleInput = (event) => {
    // strip all new lines on both typing and pasting
    const textarea = event.target;
    textarea.value = textarea.value.replace(/(\r\n|\n|\r)/gm, '');
  };

  return (
    <Wrapper noWrap={embedInDialog}>
      <div className={Styles.leadgenFormTitle}>
        <h3 className={Styles.headline}>{'Tell us about your rates'}</h3>
        <p className={Styles.description}>
          Indicate your long term engagement rate here. If your rate doesn’t match our engagement
          rates, our talent team may get in touch to discuss further.
        </p>
      </div>

      <InputContainer className={Styles.rateInputContainer} rateInput>
        <RateDualInput
          service={{ currencyCode: currency, maxRate: rate, rateType }}
          handleChange={{ onRateChange, onCurrencyChange, onRateTypeChange }}
          submitted={submitted}
          errors={errors}
        />
      </InputContainer>

      <div className={Styles.inputContainer}>
        <TextArea
          name='name'
          label='Services offered'
          placeholder='Describe the kinds of services you offer (e.g. Brand Strategy, Copywriting, etc.)'
          value={name}
          onChange={onRateDescriptionChange}
          onInput={handleInput}
          error={errors.name || ''}
        />
      </div>
      <p className={Styles.rateNotes}>You can add more rates and services later.</p>

      <div style={{ display: 'flex', marginTop: 'auto' }}>
        {!!onBack && (
          <Button type={Button.Type.WHITE} onClick={onBack}>
            {'Back'}
          </Button>
        )}
        <Button
          isLoading={loading}
          type={Button.Type.BLUE}
          style={{ width: '100%' }}
          onClick={onSubmit}>
          {'Continue'}
        </Button>
      </div>
    </Wrapper>
  );
};

export const validateRateForm = (state, setErrors) => {
  const errReq = { currency: 'Enter currency', rateType: 'Enter rate type' };
  const reqFields = ['currency', 'rateType'];
  let valid = true;
  let stateErrors = {};
  reqFields.forEach((field) => {
    if (!state[field]) {
      stateErrors = {
        ...stateErrors,
        [field]: errReq[field],
      };
      valid = false;
    }
  });

  if (state.rate?.toString().length > 12) {
    stateErrors = {
      ...stateErrors,
      rate: 'Rate should be 12 digits below.',
    };
    valid = false;
  }
  setErrors(stateErrors);

  return valid;
};

export default RateForm;
